import type { NormRect, Showcase, Step } from './demo';
import { phraseAtMs, phraseIndex, readingMs } from './camera';

/**
 * Showcase steps (`Step.showcase`, docs/plans/showcase-and-timeline.md; the founder's target is
 * tools/showcase-proto, rated 2026-10-21): where the lifted panel and its words sit on any screen,
 * and when each part comes in. Pure and zod-free, shared by the player and every export, so a
 * showcase looks and plays the same in a demo and in its video.
 *
 * The step's time, from its start:
 *  - the whole screen, the area about to be lifted outlined (`OUTLINE_AT`);
 *  - the lift: the screen gives way to the demo's background while the area travels from where
 *    it is on the screen to its place, growing (`LIFT_AT`, `LIFT_MS`);
 *  - the headline (`HEAD_AT`);
 *  - the lines: each sentence of the narration under the headline in turn, its labels coming in
 *    one after another, its ring (if any) on the panel. With a voice, a line comes when it is
 *    spoken; without, each is held for a silent reader's time.
 */

export const SHOWCASE_OUTLINE_AT = 300;
export const SHOWCASE_LIFT_AT = 1000;
export const SHOWCASE_LIFT_MS = 1100;
export const SHOWCASE_HEAD_AT = 1500;
/** On a phone the panel is out from the start (no lift from the screen): the headline comes at once. */
export const SHOWCASE_PHONE_HEAD_AT = 250;
/**
 * Reading time a sentence gets without a voice, times its plain reading time. 1, not the
 * callouts' `SILENT_READ_FACTOR`: the sentence stays on screen under a headline, and 1.4x read as
 * the panel standing still (founder, 2026-10-21).
 */
export const SHOWCASE_READ_FACTOR = 1;
/** Without a voice, the first line comes once the panel has landed and the headline is read. */
export const SHOWCASE_LINES_AT = 2400;
/**
 * On a phone nothing lifts: the panel is out at once and the headline comes at 250 ms, so the first
 * line comes as soon as the headline is seen (2400 left 2 s of nothing, founder, 2026-10-22).
 */
export const SHOWCASE_PHONE_LINES_AT = 900;
/** Each label after the first in a line comes this much later. */
export const SHOWCASE_CHIP_STAGGER_MS = 260;
/** A label comes this long after its line. */
export const SHOWCASE_CHIP_AT = 120;
/** How dark the panel round its lit part gets (an ink wash at this alpha). */
export const SHOWCASE_DIM = 0.22;

/**
 * The lit box round what a line names, in px of the panel as shown: `pad` of air (a ring needs
 * room off the words; a part with its own edge is lit to that edge, 2026-10-21) and the corner
 * radius of the hole and its ring. `assetPx` is panel px per px of the screen.
 */
export function showcaseLitShape(
  ring: NormRect,
  edge: boolean,
  asset: { w: number; h: number },
  assetPx: number,
  /** The product's corner radius in px of the screen (`productRadiusPx`). */
  corner = 14,
): { pad: number; radius: number } {
  const h = ring.h * asset.h * assetPx;
  const r = corner * assetPx;
  // The hole takes the product's own corners, grown by its air so it runs round them evenly.
  if (!edge) return { pad: 8, radius: Math.min(h / 2 + 8, r + 3) };
  return { pad: 2, radius: Math.min(h / 2, r + 2) };
}

/** A ring on the panel comes this long after its line. */
export const SHOWCASE_RING_AT = 420;
/** Time to find the new line before reading it (as a callout card, `CALLOUT_FIND_MS`). */
export const SHOWCASE_FIND_MS = 600;
/** Held on the last line before the step moves on. */
export const SHOWCASE_END_MS = 500;
/** Fades in and out of words, labels and rings. */
export const SHOWCASE_FADE_MS = 380;

export type Box = { x: number; y: number; w: number; h: number };

export interface ShowcaseLine {
  /** The sentence shown under the headline ('' when the step has no line to say). */
  text: string;
  /** Indexes into `showcase.chips` of the labels this line brings in, in order. */
  chips: number[];
  /** The part lit on the panel while this line is shown (the rest dims). */
  ring: NormRect | null;
  /** The lit part has an edge of its own (`ShowcaseChip.edge`): no ring drawn round it. */
  edge: boolean;
  /** When the line comes, from the step's start, and how long it is shown. */
  startMs: number;
  ms: number;
}

export interface ShowcasePlan {
  lines: ShowcaseLine[];
  /** The whole step: lift, lines and the last hold. */
  ms: number;
}

/**
 * The line cut into sentences: a full stop, question or exclamation mark (and a closing quote or
 * bracket) followed by a space or the end; "85.5" or "mado.show" stay whole. No lookbehind
 * (Safari before 16.4). The player's callouts cut lines the same way (`callout.ts`).
 */
export function sentences(text: string): string[] {
  const out: string[] = [];
  const re = /[.!?…]+["'”’)\]]*(?=\s|$)/g;
  let from = 0;
  const push = (s: string): void => {
    const t = s.trim();
    if (t) out.push(t);
  };
  for (let m = re.exec(text); m; m = re.exec(text)) {
    const end = m.index + m[0].length;
    push(text.slice(from, end));
    from = end;
  }
  push(text.slice(from));
  return out;
}

/**
 * The showcase's lines and when they come. `narrationMs`: the voice's clip, when it is heard
 * (null: Watch muted, or click-through's timing). A label goes to the sentence that holds its
 * phrase, else to the sentence of its own number; a sentence no label names is still shown.
 */
export function showcasePlan(
  step: Pick<Step, 'narration'> & { showcase: Showcase },
  narrationMs: number | null,
  /** Laid out for a phone (`showcaseLayout`'s box narrower than 0.9 of its height): no lift. */
  phone = false,
): ShowcasePlan {
  const linesAt = phone ? SHOWCASE_PHONE_LINES_AT : SHOWCASE_LINES_AT;
  const text = step.narration?.text.trim() ?? '';
  const ss = text ? sentences(text) : [];
  if (!ss.length) ss.push('');
  const chips = step.showcase.chips;
  const owner = chips.map((c, k) => {
    if (c.phrase) {
      const i = ss.findIndex((s) => phraseIndex(s, c.phrase!) >= 0);
      if (i >= 0) return i;
    }
    return Math.min(k, ss.length - 1);
  });
  const lines: ShowcaseLine[] = ss.map((s, i) => {
    const mine = chips.map((_, k) => k).filter((k) => owner[k] === i);
    const lit = mine.map((k) => chips[k]!).find((c) => !!c.rect);
    return { text: s, chips: mine, ring: lit?.rect ?? null, edge: !!lit?.edge, startMs: 0, ms: 0 };
  });
  if (narrationMs && narrationMs > 0 && text) {
    // Each line when the voice reaches it; the first never before the headline.
    lines.forEach((l, i) => {
      const at = i === 0 ? 0 : (phraseAtMs(text, l.text, narrationMs) ?? 0);
      l.startMs = Math.max(phone ? SHOWCASE_PHONE_LINES_AT : SHOWCASE_HEAD_AT, at);
    });
    lines.forEach((l, i) => {
      const end = i + 1 < lines.length ? lines[i + 1]!.startMs : Math.max(narrationMs, l.startMs);
      l.ms = Math.max(SHOWCASE_FADE_MS * 2, end - l.startMs);
    });
  } else {
    let t = linesAt;
    for (const l of lines) {
      l.startMs = t;
      l.ms = Math.round(
        SHOWCASE_FIND_MS +
          (l.text ? SHOWCASE_READ_FACTOR * readingMs(l.text) : 0) +
          SHOWCASE_CHIP_STAGGER_MS * Math.max(0, l.chips.length - 1),
      );
      t += l.ms;
    }
  }
  const last = lines[lines.length - 1]!;
  return { lines, ms: last.startMs + last.ms + SHOWCASE_END_MS };
}

export interface ShowcaseLayout {
  /** Headline above the panel (a phone, or a wide panel), not beside it. */
  stacked: boolean;
  /** The box the headline, line and labels sit in. */
  text: Box;
  /** Where the panel lands. On a phone a tall panel keeps the full width and is shown in part. */
  slot: Box;
  /** The panel's picture scale: px of `slot` per px of the asset. */
  scale: number;
}

/** A view narrower than this share of its height is a phone (portrait). */
const PHONE_ASPECT = 0.9;
/** A panel wider than this (w/h) sits under its headline, not beside it. */
const WIDE_PANEL = 1.3;

/**
 * Where everything goes in a `view` (px) for a panel `rect` of an `asset` (px). Side by side on
 * a desktop (words left, panel right); stacked for a phone or a wide panel. Measured on the
 * founder's target (tools/showcase-proto).
 */
export function showcaseLayout(
  view: { w: number; h: number },
  rect: NormRect,
  asset: { w: number; h: number },
): ShowcaseLayout {
  const { w: W, h: H } = view;
  const ar = (rect.w * asset.w) / Math.max(1e-6, rect.h * asset.h);
  const phone = W / Math.max(1, H) < PHONE_ASPECT;
  const stacked = phone || ar > WIDE_PANEL;
  let text: Box;
  let area: Box;
  if (!stacked) {
    text = { x: W * 0.06, y: H * 0.08, w: W * 0.37, h: H * 0.78 };
    area = { x: W * 0.47, y: H * 0.06, w: W * 0.48, h: H * 0.8 };
  } else if (phone) {
    // The panel starts a little below the words: touching, the labels sat on its edge (2026-10-21).
    text = { x: W * 0.07, y: H * 0.045, w: W * 0.86, h: H * 0.27 };
    area = { x: W * 0.05, y: H * 0.345, w: W * 0.9, h: H * 0.555 };
  } else {
    // Eyebrow, headline, dashes, a line and a row of labels: a third of the height (0.23 ran
    // the labels into the panel, 2026-10-21).
    text = { x: W * 0.06, y: H * 0.05, w: W * 0.88, h: H * 0.32 };
    area = { x: W * 0.06, y: H * 0.38, w: W * 0.88, h: H * 0.54 };
  }
  let w = area.w;
  let h = w / ar;
  if (h > area.h) {
    h = area.h;
    if (!phone) w = h * ar;
  }
  if (phone && h < area.h) {
    // A panel shorter than its room: right under the words, the two centred together, so no
    // gap opens between the labels and the panel and no empty half under it (2026-10-21).
    const shift = (area.h - h) / 2;
    text = { ...text, y: text.y + shift };
    const slot = { x: area.x + (area.w - w) / 2, y: area.y + shift, w, h };
    return { stacked, text, slot, scale: w / (rect.w * asset.w) };
  }
  const slot = { x: area.x + (area.w - w) / 2, y: area.y + (area.h - h) / 2, w, h };
  return { stacked, text, slot, scale: w / (rect.w * asset.w) };
}

/** The least zoom worth making on a phone's panel; below it, the panel is shown at its own size. */
export const SHOWCASE_ZOOM_MIN = 1.3;
/**
 * The zoom backs off a cut only while the words stay at least this tall (px). The panel is shown
 * whole-width first anyway, so a narrow panel's words are already about this size.
 */
export const SHOWCASE_READ_PX = 7;

/**
 * The panel's own camera (a phone, where the panel is small): zoomed towards `ring` so its words
 * can be read, never closer than a box 60% of the panel wide (a small button keeps its
 * surroundings), never past the panel's edges. `{ z: 1, x: 0, y: 0 }` is the panel as landed,
 * its top in view; the picture is scaled by `z` about the panel's top left, then moved by `x, y`
 * (px of the slot).
 */
export function showcasePanelZoom(
  lay: ShowcaseLayout,
  rect: NormRect,
  asset: { w: number; h: number; medianTextH?: number | undefined },
  ring: NormRect | null,
  phone: boolean,
  /** The screen's word boxes (`Asset.ink`): the view's edges never run through a line of them. */
  ink?: readonly NormRect[] | null,
): { z: number; x: number; y: number } {
  if (!phone) return { z: 1, x: 0, y: 0 };
  const { w, h } = lay.slot;
  const s = lay.scale;
  const ch = rect.h * asset.h * s;
  // What is drawn on the panel (`Asset.ink`: fields, buttons, cards, lines), in px of the panel as
  // landed (z 1). A box as wide as the panel (its own card) is crossed by any zoom.
  const boxes = (ink ?? [])
    .filter(
      (r) =>
        r.x + r.w > rect.x && r.x < rect.x + rect.w && r.y + r.h > rect.y && r.y < rect.y + rect.h,
    )
    .map((r) => ({
      x: (r.x - rect.x) * asset.w * s,
      y: (r.y - rect.y) * asset.h * s,
      w: r.w * asset.w * s,
      h: r.h * asset.h * s,
    }));
  if (!ring) return { z: 1, x: 0, y: clearEdges(0, 0, 1, w, h, null, boxes, ch) ?? 0 };
  const rx = (ring.x - rect.x) * asset.w * s;
  const ry = (ring.y - rect.y) * asset.h * s;
  const rw = ring.w * asset.w * s;
  const rh = ring.h * asset.h * s;
  const fit = Math.min(
    2.2,
    Math.max(1, Math.min((w * 0.92) / Math.max(rw, w * 0.6), (h * 0.8) / rh)),
  );
  const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
  const place = (z: number): { z: number; x: number; y: number } => ({
    z,
    x: clamp(w / 2 - (rx + rw / 2) * z, w - w * z, 0),
    y: clamp(h / 2 - (ry + rh / 2) * z, Math.min(0, h - ch * z), 0),
  });
  const words = boxes.filter((q) => q.w < w * 0.9);
  /** A side of the view runs through a field, a button or a line it shows part of. */
  const cutsSide = (v: { z: number; x: number; y: number }): boolean => {
    const l = -v.x / v.z;
    const r = (w - v.x) / v.z;
    const t = -v.y / v.z;
    const b = (h - v.y) / v.z;
    return words.some(
      (q) =>
        q.y + q.h > t &&
        q.y < b &&
        ((q.x < l - 1 && q.x + q.w > l + 1) || (q.x < r - 1 && q.x + q.w > r + 1)),
    );
  };
  // A small zoom crops the panel's sides for little gain: below it the panel only scrolls to
  // the ring (2026-10-21: a 1.17x zoom on a wide device switch cut its sentence in half).
  let v = place(fit < SHOWCASE_ZOOM_MIN ? 1 : fit);
  if (v.z > 1 && words.length && cutsSide(v)) {
    // Zoomed in less until no side cuts a line of words (founder's phone, 2026-10-21: "Anything
    // else the director sho"), as long as the words stay readable; the whole width never cuts.
    const textPx = (asset.medianTextH ?? 0) * asset.h * s;
    const least = textPx > 0 ? Math.max(1, SHOWCASE_READ_PX / textPx) : v.z;
    for (let z = v.z * 0.92; z >= least - 1e-6; z *= 0.92) {
      const u = place(z < SHOWCASE_ZOOM_MIN ? Math.max(1, least) : z);
      if (!cutsSide(u)) {
        v = u;
        break;
      }
      if (z < SHOWCASE_ZOOM_MIN) break;
    }
  }
  // Neither the top nor the foot of the view runs through what is drawn (founder's phone,
  // 2026-10-22: the screenshot "goes underneath" the panel's edge, a lit button cut at its foot):
  // the view slides to a gap; where no gap keeps the lit part whole, a little less zoom.
  const lit = { y: ry, h: rh };
  for (let z = v.z; ; z = Math.max(1, z * 0.92)) {
    const u = z === v.z ? v : place(z);
    const y = clearEdges(u.y, u.x, u.z, w, h, lit, boxes, ch);
    if (y !== null && (z === v.z || !cutsSide(u))) return { z: u.z, x: u.x, y };
    if (z === 1) break;
  }
  return v;
}

type PanelBox = { x: number; y: number; w: number; h: number };

/**
 * Where the view's offset `y` (px of the slot, the picture scaled by `z` about the panel's top)
 * and `x` put both its top and its foot in gaps between the boxes drawn on the panel (`Asset.ink` at
 * z 1), the lit part whole: the nearest such offset to `y`, or null when there is none. With
 * nothing lit the view may show a little of the screen above the area, so a heading the area's
 * own edge runs through is read whole. A box as wide as the panel, or taller than the view, is
 * crossed by any view and not counted.
 */
function clearEdges(
  y: number,
  x: number,
  z: number,
  w: number,
  h: number,
  lit: { y: number; h: number } | null,
  all: readonly PanelBox[],
  ch: number,
): number | null {
  // Only what the view's sides show part of or all of.
  const l = -x / z;
  const r = (w - x) / z;
  const boxes = all.filter(
    (q) => q.w < w * 0.9 && q.h * z < h * 0.9 && q.x + q.w > l + 1 && q.x < r - 1,
  );
  const lo = Math.min(0, h - ch * z);
  const hi = lit ? 0 : h * 0.25;
  const ok = (v: number): boolean => {
    if (v < lo - 0.5 || v > hi + 0.5) return false;
    if (lit && (lit.y * z + v < -0.5 || (lit.y + lit.h) * z + v > h + 0.5)) return false;
    const t = -v / z;
    const b = (h - v) / z;
    return !boxes.some(
      (q) => (q.y < t - 1 && q.y + q.h > t + 1) || (q.y < b - 1 && q.y + q.h > b + 1),
    );
  };
  if (ok(y)) return y;
  const tries: number[] = [];
  for (const q of boxes) {
    tries.push(4 - q.y * z, -(q.y + q.h) * z - 2, h - q.y * z + 2, h - (q.y + q.h) * z - 4);
  }
  tries.sort((a, b) => Math.abs(a - y) - Math.abs(b - y));
  return tries.find(ok) ?? null;
}
